import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Server, Monitor, RefreshCw, CheckCircle, Plus, Copy } from "lucide-react";

const AddTerminalWizard = ({
  open,
  onOpenChange,
  wizardStep,
  setWizardStep,
  newTerminal,
  setNewTerminal,
  sites,
  terminalTypes,
  unconfiguredTerminals,
  loadingUnconfigured,
  loadUnconfiguredTerminals,
  handleUseDetectedTerminal,
  handleAddTerminal,
  resetForm,
  serverUrl,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetForm(); }}>
      <DialogTrigger asChild>
        <Button onClick={() => setWizardStep(1)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("terminalMgmt.wizard.addBtn")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t("terminalMgmt.wizard.newTitle")}</DialogTitle>
          <DialogDescription>
            {t("terminalMgmt.wizard.stepLabel", { step: wizardStep, desc: wizardStep === 1 ? t("terminalMgmt.wizard.step1Desc") : wizardStep === 2 ? t("terminalMgmt.wizard.step2Desc") : t("terminalMgmt.wizard.step3Desc") })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${wizardStep >= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                {step}
              </div>
              {step < 3 && <div className={`h-0.5 w-12 ${wizardStep > step ? "bg-primary" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        <div className="py-4">
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 border border-border rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium text-foreground">{t("terminalMgmt.wizard.serverUrlLabel")}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-900 rounded px-3 py-2">
                  <code className="text-green-400 font-mono text-sm flex-1">{serverUrl}</code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 h-7 px-2"
                    onClick={() => { navigator.clipboard.writeText(serverUrl); toast.success(t("terminalMgmt.urlCopied")); }}
                  >
                    <Copy className="h-3 w-3 mr-1" /> {t("terminalMgmt.copy")}
                  </Button>
                </div>
              </div>

              <div className="space-y-3 text-sm text-foreground">
                <p className="font-medium text-foreground">{t("terminalMgmt.wizard.configStepsTitle")}</p>
                <ol className="list-decimal ml-5 space-y-2">
                  <li>{t("terminalMgmt.wizard.cfgStep1Pre")}<strong>{t("terminalMgmt.wizard.cfgStep1Strong")}</strong></li>
                  <li>{t("terminalMgmt.wizard.cfgStep2")}</li>
                  <li>{t("terminalMgmt.wizard.cfgStep3Pre")}<strong>{t("terminalMgmt.wizard.cfgStep3Strong")}</strong>{t("terminalMgmt.wizard.cfgStep3Post")}</li>
                  <li>{t("terminalMgmt.wizard.cfgStep4Pre")}<strong>{t("terminalMgmt.wizard.cfgStep4Strong")}</strong>{t("terminalMgmt.wizard.cfgStep4Post")}</li>
                  <li>{t("terminalMgmt.wizard.cfgStep5")}</li>
                </ol>
                <div className="p-3 bg-amber-950/20 border border-amber-900/40 rounded text-xs text-amber-400">
                  {t("terminalMgmt.wizard.autoSendNote")}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => { setWizardStep(2); loadUnconfiguredTerminals(); }} className="bg-blue-600 hover:bg-blue-700">
                  {t("terminalMgmt.wizard.nextDetect")}
                </Button>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("terminalMgmt.wizard.detectHint")}
              </p>

              <div className="space-y-3">
                {unconfiguredTerminals.length === 0 ? (
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <Monitor className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm">{t("terminalMgmt.wizard.noneDetected")}</p>
                    <p className="text-gray-400 text-xs mt-1">{t("terminalMgmt.wizard.checkPowered")}</p>
                  </div>
                ) : (
                  unconfiguredTerminals.map((terminal) => (
                    <div key={terminal.serial_number} className="flex items-center justify-between bg-green-950/20 rounded-lg p-3 border border-green-900/40">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <div>
                          <div className="font-medium text-foreground">{terminal.serial_number}</div>
                          <div className="text-sm text-muted-foreground">
                            IP: {terminal.ip_address} · {terminal.last_seen ? new Date(terminal.last_seen).toLocaleString() : ""}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                        onClick={() => handleUseDetectedTerminal(terminal)}
                      >
                        {t("terminalMgmt.wizard.useThis")}
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" size="sm" onClick={loadUnconfiguredTerminals} disabled={loadingUnconfigured}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingUnconfigured ? "animate-spin" : ""}`} />
                  {t("common.refresh")}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setWizardStep(1)}>{t("terminalMgmt.wizard.back")}</Button>
                  <Button onClick={() => setWizardStep(3)} className="bg-blue-600 hover:bg-blue-700">
                    {t("terminalMgmt.wizard.enterManually")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-4">
              {newTerminal.serial_number && (
                <div className="p-3 bg-green-950/20 border border-green-900/40 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t("terminalMgmt.wizard.detectedBanner", { serial: newTerminal.serial_number })}</p>
                    <p className="text-xs text-muted-foreground">{t("terminalMgmt.wizard.completeInfo")}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    {t("terminalMgmt.form.nameLabel")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={newTerminal.name}
                    onChange={(e) => setNewTerminal({ ...newTerminal, name: e.target.value })}
                    placeholder={t("terminalMgmt.form.namePlaceholder")}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial_number" className="text-sm font-medium">
                    {t("terminalMgmt.form.serialLabel")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="serial_number"
                    value={newTerminal.serial_number}
                    onChange={(e) => setNewTerminal({ ...newTerminal, serial_number: (e.target.value || "").toUpperCase().trim() })}
                    placeholder={t("terminalMgmt.form.serialPlaceholder")}
                    className="h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("terminalMgmt.form.siteLabel")} <span className="text-red-500">*</span></Label>
                  <Select
                    value={newTerminal.site_id ? newTerminal.site_id.toString() : ""}
                    onValueChange={(value) => setNewTerminal({ ...newTerminal, site_id: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("terminalMgmt.form.sitePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id.toString()}>
                          📍 {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("terminalMgmt.form.typeLabel")}</Label>
                  <Select
                    value={newTerminal.terminal_type}
                    onValueChange={(value) => setNewTerminal({ ...newTerminal, terminal_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {terminalTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="model">{t("terminalMgmt.form.modelLabel")}</Label>
                  <Input
                    id="model"
                    value={newTerminal.model}
                    onChange={(e) => setNewTerminal({ ...newTerminal, model: e.target.value })}
                    placeholder={t("terminalMgmt.form.modelPlaceholder")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="firmware">{t("terminalMgmt.form.firmwareLabel")}</Label>
                  <Input
                    id="firmware"
                    value={newTerminal.firmware_version}
                    onChange={(e) => setNewTerminal({ ...newTerminal, firmware_version: e.target.value })}
                    placeholder={t("terminalMgmt.form.firmwarePlaceholder")}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setWizardStep(2)}>{t("terminalMgmt.wizard.back")}</Button>
                <Button
                  onClick={handleAddTerminal}
                  className="px-8 bg-blue-600 hover:bg-blue-700"
                  disabled={!newTerminal.name || !newTerminal.site_id || !newTerminal.serial_number}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("terminalMgmt.wizard.registerBtn")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddTerminalWizard;
